import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckSquare, Clock, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { api, apiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, KpiCard, PageHeader, StatusBadge, TableSkeleton } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { SelectField } from "@/components/forms/Fields";
import { TaskForm } from "@/components/forms/OpsForms";

export default function Tasks() {
  const { meta, user, can } = useAuth();
  const [data, setData] = useState(null);
  const [scope, setScope] = useState("mine");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);

  const load = useCallback(async () => {
    setData(null);
    try {
      const { data: res } = await api.get("/tasks", {
        params: { scope, status: status !== "all" ? status : undefined },
      });
      setData(res);
    } catch (e) {
      toast.error(apiError(e));
      setData({ items: [] });
    }
  }, [scope, status]);

  useEffect(() => {
    load();
  }, [load, refresh]);

  const update = async (id, value) => {
    try {
      await api.patch(`/tasks/${id}/status`, { status: value });
      toast.success(`Task marked ${value}`);
      setRefresh((r) => r + 1);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/tasks/${id}`);
      toast.success("Task removed");
      setRefresh((r) => r + 1);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const items = data?.items || [];
  const overdue = items.filter((t) => t.is_overdue);
  const openCount = items.filter((t) => t.status === "Open").length;
  const done = items.filter((t) => t.status === "Completed").length;

  return (
    <div className="space-y-6">
      <PageHeader
        meta="Follow-ups"
        title="Tasks"
        subtitle="Calls, client follow-ups, feedback chases and joining confirmations in one place."
        actions={
          can("tasks", "create") && (
            <Button onClick={() => setOpen(true)} data-testid="create-task-button" className="h-9 gap-2 rounded-full bg-blue-600 px-4 text-xs hover:bg-blue-500">
              <Plus className="h-3.5 w-3.5" /> Create task
            </Button>
          )
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Open" value={openCount} icon={CheckSquare} testid="tasks-kpi-open" />
        <KpiCard label="Overdue" value={overdue.length} icon={AlertTriangle} tone="rose" testid="tasks-kpi-overdue" />
        <KpiCard label="Completed" value={done} icon={CheckSquare} tone="emerald" />
        <KpiCard label="Total" value={items.length} icon={Clock} tone="slate" />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <SelectField value={scope} onChange={setScope} className="w-48" testid="tasks-scope-filter"
          options={[{ value: "mine", label: "My tasks" }, { value: "team", label: "Whole team" }]} />
        <SelectField value={status} onChange={setStatus} allowAll className="w-48" testid="tasks-status-filter"
          options={["Open", "In Progress", "Completed", "Cancelled"]} placeholder="Status" />
      </div>

      {!data ? (
        <TableSkeleton rows={6} cols={4} />
      ) : items.length === 0 ? (
        <EmptyState icon={CheckSquare} title="Nothing on your list" description="Create a task to track a follow-up." testid="tasks-empty-state" />
      ) : (
        <div className="surface divide-y divide-slate-200 rounded-md">
          {items.map((t) => (
            <div key={t.id} className="flex items-start gap-3 p-4" data-testid="task-row">
              <Checkbox
                checked={t.status === "Completed"}
                onCheckedChange={(v) => update(t.id, v ? "Completed" : "Open")}
                data-testid="task-checkbox"
                className="mt-0.5 border-slate-300"
              />
              <div className="min-w-0 flex-1">
                <p className={`text-sm ${t.status === "Completed" ? "text-slate-500 line-through" : "text-slate-900"}`}>
                  {t.title}
                </p>
                {t.description && <p className="mt-0.5 text-xs text-slate-500">{t.description}</p>}
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-400">
                  <span>{t.task_type}</span>
                  <span>{t.assignee_name || "Unassigned"}</span>
                  {t.candidate_name && <span>{t.candidate_name}</span>}
                  {t.job_title && <span>{t.job_title}</span>}
                  <span className={t.is_overdue ? "text-rose-600" : ""}>Due {fmtDate(t.due_date)}</span>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {t.is_overdue && <StatusBadge value="Overdue" />}
                <StatusBadge value={t.priority} />
                <StatusBadge value={t.status} />
                <button onClick={() => remove(t.id)} aria-label="Delete task" data-testid="task-delete-button" className="text-slate-400 hover:text-rose-600">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <TaskForm open={open} onOpenChange={setOpen} onSaved={() => setRefresh((r) => r + 1)} />
    </div>
  );
}
